import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MessageSquare, TrendingUp, ShoppingCart, Send, Wifi, WifiOff, Clock, Database, AlertCircle, X, Hash, Key, Info, ShieldAlert, Loader2, RefreshCw, Copy, Check, Trash2, RotateCcw, Settings, Link } from 'lucide-react';
import { initializeApp, getApps, getApp, deleteApp } from "firebase/app";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, addDoc, onSnapshot, query, limit, getDocs, writeBatch, doc, setDoc } from "firebase/firestore";

// =================================================================
// [필수 1] 여기에 본인의 Firebase 설정값을 붙여넣으세요!
// =================================================================
const firebaseConfig = {
  apiKey: "AIzaSyDNd9_hDA77GXtPx5VnA_q9M4DhFnSEhEc",
  authDomain: "my-wallet-e67ea.firebaseapp.com",
  projectId: "my-wallet-e67ea",
  storageBucket: "my-wallet-e67ea.firebasestorage.app",
  messagingSenderId: "836389125598",
  appId: "1:836389125598:web:5936249bd5045cce8cb14f",
  measurementId: "G-N329SG0S7Y"
};

// --- Firebase 초기화 ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const projectId = firebaseConfig.projectId; 
const DATA_COLLECTION_PATH = 'balance_history';

// --- Ad Component (스크립트 자동 로드 기능 포함) ---
const AdComponent = () => {
  useEffect(() => {
    // 1. 애드센스 스크립트가 없으면 강제로 심어줍니다.
    if (!window.adsbygoogle) {
      const script = document.createElement('script');
      // [필수 2] 아래 주소의 client= 뒤에 본인의 ca-pub 코드를 넣으세요!
      script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9553226375603850"; 
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }

    // 2. 광고 표시 요청
    try {
      if (process.env.NODE_ENV !== 'development') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense Error:", e);
    }
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0', minHeight: '280px', backgroundColor: '#f8fafc', borderRadius: '8px', alignItems: 'center', overflow: 'hidden' }}>
      {/* [필수 3] 승인이 완료되면 아래 <ins> 태그 주석을 풀고,
          data-ad-client와 data-ad-slot을 채워 넣으세요.
      */}
      {/* <ins className="adsbygoogle"
           style={{ display: 'block', width: '100%', height: '100%' }}
           data-ad-client="ca-pub-XXXXXXXXXXXXXXXX" 
           data-ad-slot="1234567890" 
           data-ad-format="auto"
           data-full-width-responsive="true"></ins>
      */}
      
      <div className="text-center p-4 text-slate-400 text-xs">
        <p className="font-bold mb-1">광고 영역</p>
        <p>Google AdSense 승인 대기 중...</p>
        <p className="text-[10px] mt-1 text-slate-300">(스크립트 자동 로드됨)</p>
      </div>
    </div>
  );
};

// --- Header Component ---
const Header = ({ onBuyClick, status }) => (
  <header className="bg-slate-900 text-white p-4 shadow-lg sticky top-0 z-50 border-b border-slate-700">
    <div className="container mx-auto flex justify-between items-center">
      <div className="flex items-center space-x-2">
        <TrendingUp className="text-green-400 h-8 w-8" />
        <div>
          <h1 className="text-xl font-bold tracking-tighter">Lino Donboksa</h1>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-400">실시간 선물 잔고 공개</span>
            {status === 'online' ? (
              <span className="flex items-center text-[10px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded-full border border-green-800">
                <Wifi className="w-3 h-3 mr-1" /> LIVE
              </span>
            ) : (
              <span className="flex items-center text-[10px] text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded-full border border-red-800">
                <WifiOff className="w-3 h-3 mr-1" /> OFFLINE
              </span>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={onBuyClick}
        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-bold flex items-center transition-transform active:scale-95 shadow-[0_0_15px_rgba(34,197,94,0.5)]"
      >
        <ShoppingCart className="mr-2 h-4 w-4" />
        <span className="hidden sm:inline">봇 구매</span>
        <span className="sm:hidden">구매</span>
      </button>
    </div>
  </header>
);

// --- Chart Component (Date Updated) ---
const BalanceChart = ({ data, status, lastUpdate }) => {
  const currentBalance = data.length > 0 ? data[data.length - 1].usdt : 0;
  const startBalance = data.length > 0 ? data[0].usdt : 0;
  const pnl = currentBalance - startBalance;
  const pnlPercent = startBalance > 0 ? ((pnl / startBalance) * 100).toFixed(2) : 0;

  return (
    <div className="bg-white rounded-xl shadow-xl p-6 mb-8 border border-slate-100 relative overflow-hidden">
      {status === 'offline' && (
        <div className="absolute top-0 left-0 w-full h-1 bg-red-500 animate-pulse" />
      )}
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-slate-500 text-sm font-medium uppercase tracking-wide">Current Wallet Balance (USDT)</h2>
          <div className="flex items-baseline space-x-3">
            <span className="text-4xl font-extrabold text-slate-900">${currentBalance.toLocaleString()}</span>
            <span className={`text-lg font-semibold ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {pnl >= 0 ? '+' : ''}{pnlPercent}% (오늘)
            </span>
          </div>
        </div>
        <div className="flex space-x-2 text-right">
           <div className="flex flex-col items-end">
             <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
               <span className={`w-2 h-2 mr-1 rounded-full ${status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
               {status === 'online' ? '실시간 연동중' : '연결 대기중'}
             </span>
             <span className="text-[10px] text-slate-400 mt-1 flex items-center">
               <Clock className="w-3 h-3 mr-1" />
               {lastUpdate ? lastUpdate : '데이터 없음'}
             </span>
           </div>
        </div>
      </div>

      <div className="h-[400px] w-full relative">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis 
                dataKey="shortTime" // 짧은 날짜+시간 사용
                stroke="#94a3b8" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                minTickGap={30}
              />
              <YAxis 
                domain={['auto', 'auto']} 
                stroke="#94a3b8" 
                fontSize={12}
                tickFormatter={(value) => `$${value}`}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                itemStyle={{ color: '#4ade80' }}
                labelStyle={{ color: '#94a3b8', marginBottom: '0.25rem' }}
                // 툴팁에는 전체 날짜 표시
                labelFormatter={(label, payload) => {
                    if (payload && payload.length > 0) {
                        return payload[0].payload.fullTime;
                    }
                    return label;
                }}
                formatter={(value) => [`$${value}`, 'USDT']}
              />
              <Line 
                type="monotone" 
                dataKey="usdt" 
                stroke="#2563eb" 
                strokeWidth={3} 
                dot={false}
                activeDot={{ r: 8, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }}
                animationDuration={500}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50/50 rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
             <WifiOff className="w-12 h-12 mb-4 text-slate-300" />
             <h3 className="text-lg font-bold text-slate-600 mb-2">데이터 대기중</h3>
             <p className="text-sm text-slate-500">봇이 데이터를 보내면 차트가 그려집니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Comment Component ---
const CommentSection = ({ user }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'comments'), limit(50));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      msgs.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      setComments(msgs);
      setTimeout(() => {
        if(scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 100);
    });

    return () => unsubscribe();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !nickname.trim()) return;
    setLoading(true);

    try {
      await addDoc(collection(db, 'comments'), {
        nickname: nickname,
        text: newComment,
        createdAt: Date.now(), 
        userId: user.uid.slice(0, 5) 
      });
      setNewComment('');
    } catch (err) {
      console.error("Error adding comment:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden flex flex-col h-[500px]">
      <div className="bg-slate-50 p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-bold text-slate-700 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
          익명 채팅 / 응원
        </h3>
        <span className="text-xs text-slate-400">로그인 불필요</span>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50" ref={scrollRef}>
        {comments.length === 0 && (
          <div className="text-center text-slate-400 py-10">첫 번째 메시지를 남겨보세요!</div>
        )}
        {comments.map((msg) => (
          <div key={msg.id} className="flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-baseline space-x-2">
              <span className="font-bold text-sm text-slate-800">{msg.nickname}</span>
              <span className="text-[10px] text-slate-400">#{msg.userId}</span>
            </div>
            <div className="bg-white p-2.5 rounded-r-lg rounded-bl-lg border border-slate-200 shadow-sm mt-1 text-sm text-slate-700 inline-block w-fit max-w-[90%] break-words">
              {msg.text}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-white border-t border-slate-200">
        <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
          <input
            type="text"
            placeholder="닉네임"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={10}
            required
          />
          <div className="flex space-x-2">
            <input
              type="text"
              placeholder="메시지를 입력하세요..."
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              required
            />
            <button 
              type="submit" 
              disabled={loading}
              className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Main App ---
export default function App() {
  const [user, setUser] = useState(null);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('offline'); 
  const [showModal, setShowModal] = useState(false);

  // 1. Auth Init
  useEffect(() => {
    signInAnonymously(auth).catch(console.error);
    return onAuthStateChanged(auth, setUser);
  }, []);

  // 2. Data Sync
  useEffect(() => {
    if (!user) return;
    
    const q = query(collection(db, DATA_COLLECTION_PATH), limit(200));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const history = snapshot.docs.map(doc => doc.data());
      const validHistory = history.filter(h => h.timestamp && typeof h.timestamp === 'number');
      validHistory.sort((a, b) => a.timestamp - b.timestamp);
      
      const formattedHistory = validHistory.map(item => {
        const date = new Date(item.timestamp);
        return {
          ...item,
          // 1. 짧은 시간 (축에 표시용): 11/28 14:30
          shortTime: `${date.getMonth() + 1}/${date.getDate()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
          // 2. 긴 시간 (툴팁용): 2024년 11월 28일 14:30:00
          fullTime: date.toLocaleString('ko-KR')
        };
      });
      
      setBalanceHistory(formattedHistory);

      if (validHistory.length > 0) {
        const lastItem = validHistory[validHistory.length - 1];
        const lastTime = lastItem.timestamp;
        const now = Date.now();
        // 3분 이내 데이터가 있으면 온라인 간주
        if (now - lastTime < 180000) {
          setConnectionStatus('online');
        } else {
          setConnectionStatus('offline');
        }
      } else {
        setConnectionStatus('offline');
      }
    }, (error) => {
        console.error("Data sync error:", error);
        setConnectionStatus('offline');
    });

    return () => unsubscribe();
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <Header onBuyClick={() => setShowModal(true)} status={connectionStatus} />

      <div className="container mx-auto mt-8 px-4">
        <div className="flex flex-col lg:flex-row justify-center items-start lg:space-x-8">
          
          {/* Left Ad */}
          <div className="hidden lg:block w-64 flex-shrink-0">
             <AdComponent />
          </div>

          <main className="flex-1 w-full max-w-3xl">
            <div className={`border rounded-lg p-4 mb-6 flex items-start bg-blue-50 border-blue-200`}>
              <Database className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-bold text-blue-800">
                    BitMaster Trading Live
                </p>
                <p className="text-slate-600">
                    바이낸스 선물 잔고 실시간 현황판입니다. (Project: {projectId})
                </p>
              </div>
            </div>

            <BalanceChart 
                data={balanceHistory} 
                status={connectionStatus} 
                lastUpdate={balanceHistory.length > 0 ? balanceHistory[balanceHistory.length-1].fullTime : null}
            />
            
            <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-slate-100">
              <h3 className="text-xl font-bold mb-4">🤖 Alpha-One 봇 특징</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-slate-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  24시간 365일 무중단 트레이딩
                </li>
                <li className="flex items-center text-slate-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  변동성 돌파 전략 및 RSI 역추세 매매 혼합
                </li>
              </ul>
            </div>

            <CommentSection user={user} />
          </main>

          {/* Right Ad */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <AdComponent />
          </div>
          
        </div>
      </div>

      <div className="lg:hidden p-4">
        <div className="w-full h-32 bg-slate-200 rounded flex items-center justify-center border-2 border-dashed border-slate-300">
          <span className="text-slate-400 text-sm">모바일 하단 광고 영역</span>
          <AdComponent />
        </div>
      </div>

      <footer className="bg-slate-900 text-slate-400 py-8 mt-12 text-center text-sm">
        <p>© 2024 BitMaster Trading. All rights reserved.</p>
        <p className="mt-2 text-xs text-slate-600">
          본 사이트는 개인의 투자 기록을 보여주는 포트폴리오 용도이며, <br/>
          직접적인 투자를 권유하거나 금전을 요구하지 않습니다. 투자의 책임은 본인에게 있습니다.
        </p>
      </footer>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Alpha-One 평생 이용권</h2>
              <p className="text-slate-600 mb-6">지금 구매하고 상위 1%의 트레이딩 로직을 경험하세요.</p>
              
              <div className="bg-slate-50 p-4 rounded-lg mb-6 border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-500">정가</span>
                  <span className="text-slate-400 line-through">1,000 USDT</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-900 font-bold">할인가</span>
                  <span className="text-2xl font-extrabold text-green-600">300 USDT</span>
                </div>
              </div>

              <button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg shadow-lg mb-3 transition-colors">
                USDT로 결제하기
              </button>
              <button className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-lg transition-colors">
                상담 문의하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}